import type React from "react";
import { useMemo } from "react";
import type {
    EViewBySwitcherItems,
} from "../ChannelsOverviewBarChart/components/view-by-switcher/ViewBySwitcher";
import ViewBySwitcher from "../ChannelsOverviewBarChart/components/view-by-switcher/ViewBySwitcher";
import { ChannelsOverviewBarChartSwitcher } from "../ChannelsOverviewBarChart/ChannelsOverviewBarChartSwitcher";
import { PngDownload } from "app/pages/website-analysis/traffic-sources/mmx/components/common/PngDownload";
import { WidgetTitle } from "app/components/widget-title/WidgetTitle";
import CountryService from "app/services/CountryService";
import DurationService from "app/services/DurationService";
import { translate } from "app/filters/i18nFilter";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { DataFormat } from "../ChannelsOverviewBarChart/ChannelsOverviewBarChartContainerFormat";

interface TitleActionsProps {
    isCompare: boolean;
    viewBy: EViewBySwitcherItems;
    onViewChange: (selectedViewBy: EViewBySwitcherItems) => void;
    onFormatChange: (selectedFormat) => void;
    chartRef: React.RefObject<HTMLDivElement | null>;
    dataFormat: DataFormat;
}

const TitleActions: React.FC<TitleActionsProps> = ({
    isCompare,
    viewBy,
    onViewChange,
    onFormatChange,
    chartRef,
    dataFormat,
}) => {
    const params = proNavigator.getParams();
    const { country, duration, comparedDuration, webSource } = params;
    const durationObject = DurationService.getDurationData(duration, comparedDuration);
    const getDateRange = (durationObject) => [durationObject.raw.from, durationObject.raw.to];
    const getCompareDateRange = (durationObject) => [durationObject.raw.compareFrom, durationObject.raw.compareTo];
    const [fromDate, toDate] = getDateRange(durationObject);
    const [compareFromDate, compareToDate] = getCompareDateRange(durationObject);
    const title = isCompare
        ? translate("metric.engagementTrafficSourcesOverview.pop-compare.title")
        : translate("metric.engagementTrafficSourcesOverview.pop.title");

    const subTitleFilters = useMemo(
        () => [
            {
                filter: "date",
                value: {
                    from: fromDate.valueOf(),
                    to: toDate.valueOf(),
                    useRangeDisplay: fromDate.format("YYYY-MM") !== toDate.format("YYYY-MM"),
                    compareFrom: compareFromDate.valueOf(),
                    compareTo: compareToDate.valueOf(),
                },
            },
            {
                filter: "country",
                countryCode: country,
                value: CountryService.getCountryById(country)?.text,
            },
            {
                filter: "webSource",
                value: webSource,
            },
        ],
        [fromDate, toDate, compareFromDate, compareToDate, country, webSource],
    );

    return (
        <>
            <WidgetTitle
                title={title}
                tooltip={translate("metric.engagementTrafficSourcesOverview.pop.tooltip")}
                filters={subTitleFilters}
            />
            {isCompare ? (
                <>
                    <ViewBySwitcher
                        onViewChange={onViewChange}
                        selectedView={viewBy}
                    />
                    <ChannelsOverviewBarChartSwitcher
                        onClick={onFormatChange}
                        dataFormat={dataFormat}
                    />
                </>
            ) : null}
            <PngDownload
                selectedMetricName={title}
                chartRef={chartRef}
            />
        </>
    );
};

export default TitleActions;
